import React, { Suspense, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router'
import { useMount } from 'react-use'

import useAppDispatch from '../../../hooks/useAppDispatch'
import { useAppStateQuery } from '../../../hooks/useAppStateQuery'
import { useCompanyDashboardSidebarLinks } from '../../../hooks/useCompanyDashboardSidebarLinks'
import { useCurrentCompanyServicesQuery } from '../../../hooks/useCompanyServicesQuery'
import { useCurrentUserOrFail } from '../../../hooks/useCurrentUserOrFail'
import { useSessionMetadataOrFail } from '../../../hooks/useSessionMetadataOrFail'
import { ROUTES } from '../../../lib/routes'
import RecommendedPartnerModalContainer from '../../../pages/professionalNetworkRelationships/components/RecommendedPartnerModal/RecommendedPartnerModalContainer'
import { LAYOUTS, setLayout } from '../../../redux/slices/layout'
import Banners from '../../banners/Banners'
import CompanyProfileModal from '../../misc/CompanyProfile/components/CompanyProfileModal'
import Main from '../../misc/Main'
import PageContent from '../../misc/PageContent'
import Payments from '../../misc/Payments/Payments'
import SentryErrorBoundary from '../../misc/SentryErrorBoundary'
import BookMeetingModal from '../../modals/BookMeetingModal'
import IcaewModals from '../../modals/IcaewModals/IcaewModals'
import SelectServicesModal from '../../modals/SelectServicesModal'
import VideoModal from '../../modals/VideoModal'
import LoadingWidget from '../../widgets/LoadingWidget'
import Footer from './components/Footer'
import Navbar from './components/Navbar/Navbar'
import Sidebar from './components/sidebar/Sidebar'

interface Props {
  children: React.ReactNode
  padded?: boolean
}

const CompanyDashboardLayout: React.FC<Props> = ({
  children,
  padded = true,
}) => {
  const companyServicesQuery = useCurrentCompanyServicesQuery()
  const currentUser = useCurrentUserOrFail()
  const location = useLocation()
  const sidebarLinks = useCompanyDashboardSidebarLinks()
  const dispatch = useAppDispatch()
  const [showServicesModal, setShowServicesModal] = useState(false)
  const sessionMetadata = useSessionMetadataOrFail()

  useMount(() => {
    dispatch(setLayout(LAYOUTS.companyDashboard))
  })

  useEffect(() => {
    // Change to true when we can, but never change to false
    if (
      !showServicesModal &&
      currentUser.registeredViaReferralInvite &&
      companyServicesQuery.data &&
      companyServicesQuery.data.length <= 1
    ) {
      setShowServicesModal(true)
    }
  }, [showServicesModal, companyServicesQuery.data, currentUser])

  const appStateQuery = useAppStateQuery()

  if (currentUser.needsToCompleteWalkthrough) {
    return <Navigate to={ROUTES.walkthrough} replace />
  }

  if (appStateQuery.isLoading) {
    return <LoadingWidget />
  }

  const showIcaewModals =
    sessionMetadata.icaewModals &&
    Object.values(sessionMetadata.icaewModals).some(
      (shouldShow) => !!shouldShow,
    ) &&
    !showServicesModal

  return (
    <>
      <Payments />

      <Banners />

      {showServicesModal && (
        <SelectServicesModal onHide={() => setShowServicesModal(false)} />
      )}

      {showIcaewModals && <IcaewModals />}

      <div className="wrapper">
        <Sidebar items={[{ pages: sidebarLinks }]} />

        <Main>
          <Navbar />

          <PageContent padded={padded}>
            <SentryErrorBoundary key={location.pathname}>
              <Suspense>{children}</Suspense>
            </SentryErrorBoundary>
          </PageContent>

          <Footer />
        </Main>

        <VideoModal />
        <BookMeetingModal />
        <RecommendedPartnerModalContainer />
        <CompanyProfileModal />
      </div>
    </>
  )
}

export default CompanyDashboardLayout
